import { AppShell, NavLink, ScrollArea, Stack } from "@mantine/core";
import { createElement } from "react";
import { useTranslation } from "react-i18next";

import { ICON_SIZE } from "@mountayaapp/theme";
import classes from "@mountayaapp/theme/railNavLink.module.css";

import { normalizeHref } from "../../content/rules/href";
import {
  getNavManifest,
  type NavItem,
  type NavNode,
  resolveTabItem,
  type Tab,
} from "../../content/rules/registry";
import { resolveLanguage } from "../../locale/rules/resolveLanguage";
import { useDocsPathname } from "../providers/DocsPathnameContext";

/**
 * One page in the rail: a full-reload anchor that marks itself when it is the active one.
 *
 * `active` and `aria-current` both, because they answer different readers. Mantine's `active`
 * is paint — it tints the row and nothing else reaches the accessibility tree — so a reader
 * navigating the sidebar by keyboard or screen reader heard thirty links and no indication of
 * which one they were standing on. `aria-current="page"` is the one attribute that says so, and
 * it is not something an axe run reports as a violation, which is why the a11y gate was green
 * over it.
 */
const PageLink = ({ current, item }: { current: string; item: NavItem }) => {
  const isCurrent = normalizeHref(item.href) === current;

  return (
    <NavLink
      active={isCurrent}
      aria-current={isCurrent ? "page" : undefined}
      classNames={{ root: classes.item }}
      component="a"
      href={item.href}
      label={item.title}
    />
  );
};

/**
 * RailNode draws one entry: a page, or a nested section with its pages under it.
 *
 * A section headed by a page is BOTH — the heading navigates to that page and marks itself
 * when it is the one being read, and the pages under it elaborate it. Mantine's `NavLink`
 * carries both jobs, so a reader can reach `map-controls` without first choosing between
 * opening the section and visiting it.
 */
const RailNode = ({
  current,
  label,
  node,
}: {
  current: string;
  label: (key: string) => string;
  node: NavNode;
}) => {
  if (!("items" in node)) {
    return <PageLink current={current} item={node} />;
  }

  const { items, labelKey } = node;
  const holdsActive = items.some((child) => normalizeHref(child.href) === current);

  return (
    <NavLink
      // No `active` and no `aria-current`: a section heading is a disclosure BUTTON rather
      // than a destination, and marking a control as the current page would announce one to
      // a screen reader. The page under it carries both marks instead.
      childrenOffset={0}
      classNames={{ children: classes.children, root: classes.item }}
      component="button"
      defaultOpened={holdsActive}
      label={label(labelKey)}
    >
      {items.map((child) => (
        <PageLink current={current} item={child} key={child.href} />
      ))}
    </NavLink>
  );
};

/**
 * DocsNavbarProps carry the active tab whose curated groups the sidebar lists. The
 * shell renders the navbar only for tabs whose layout is `sidebar`, and the tab id
 * filters the manifest so the rail shows that tab's sections alone.
 */
interface DocsNavbarProps {
  tab: Tab;
}

/**
 * DocsNavbar is the docs sidebar: a scrollable rail of the active tab's curated
 * navigation groups, headed by the pinned entry section the manifest returns for
 * every tab, so the way into the platform is present wherever a reader lands. Each
 * group is a collapsible section headed by its icon and localized label, expanded
 * by default when it holds the active page, with its pages hanging off a guide rule
 * as full-reload anchors that mark the current one — the docs app serves static
 * pages with no client router, so links are plain anchors. The brand, theme switch,
 * and Map Studio link live in the persistent header rather than here, so the navbar
 * is just the navigation.
 *
 * A group may nest ONE level: a section of pages under a heading that is either a page of its
 * own (the control set, headed by the page that documents the stack) or a label (the two
 * elements an itinerary id is enough for, which nothing documents as a pair). The nested
 * section opens the same way a group does — by default when it holds the active page — so a
 * reader never has to open two disclosures to see where they are.
 *
 * Its box, density, and item states come from the shared rail chrome in the theme, the
 * same rules the application's account and organization rail renders under, so the two
 * page rails read as one surface.
 */
export const DocsNavbar = ({ tab }: DocsNavbarProps) => {
  const { t } = useTranslation("docs");
  const pathname = useDocsPathname();
  const language = resolveLanguage(pathname);
  // A dropdown tab holds several destinations and each owns its own groups, so the rail
  // shows the one the reader is inside rather than all of them at once: the SDKs rail is
  // one language's packages, which is the level the dropdown exists to introduce.
  const slug = pathname.split("/").filter(Boolean).slice(1).join("/");
  const item = resolveTabItem(tab, slug);
  const groups = getNavManifest(language, tab.id, item?.id);
  const current = normalizeHref(pathname);

  return (
    // The region AppShell reserves is already a <nav>, so the list inside it is a plain
    // Stack: a second navigation landmark nested in the first names nothing new and
    // leaves a screen-reader user two identical, unlabelled entries where the page has
    // one rail. The label is what tells that one entry apart from the header's tab row
    // and the reference rail, which name themselves the same way.
    <AppShell.Navbar aria-label={t("nav.pages")} className={classes.rail}>
      <AppShell.Section component={ScrollArea} grow type="scroll">
        <Stack gap="md">
          {groups.map((group) => {
            // The group opens by default when it holds the active page, so the
            // current link is always visible without the reader hunting for it.
            const isActiveGroup = group.items.some((item) => normalizeHref(item.href) === current);

            return (
              <NavLink
                aria-label={t("nav.toggleGroup", { group: t(group.labelKey) })}
                childrenOffset={0}
                classNames={{ children: classes.children, root: classes.section }}
                defaultOpened={isActiveGroup}
                fw={600}
                key={group.labelKey}
                label={t(group.labelKey)}
                leftSection={createElement(group.icon, { size: ICON_SIZE.sm })}
              >
                {group.tree.map((node) => (
                  <RailNode
                    current={current}
                    key={"items" in node ? node.labelKey : node.href}
                    label={t}
                    node={node}
                  />
                ))}
              </NavLink>
            );
          })}
        </Stack>
      </AppShell.Section>
    </AppShell.Navbar>
  );
};
